import React, { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { productCatalogBySlug } from '../data/productCatalog';

const createStyles = () => ({
  mainContainer: {
    background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
    minHeight: '100vh',
    padding: '2rem 0'
  },
  container: {
    maxWidth: '1200px',
    margin: '0 auto',
    padding: '0 2rem',
    fontFamily: "'Inter', 'Segoe UI', sans-serif"
  },
  hero: {
    background: 'linear-gradient(135deg, var(--theme-hero-start) 0%, var(--theme-hero-end) 100%)',
    color: 'white',
    padding: '5rem 2rem',
    textAlign: 'center',
    borderRadius: '2rem',
    marginBottom: '4rem',
    position: 'relative',
    overflow: 'hidden'
  },
  heroOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'radial-gradient(circle at 50% 50%, rgba(255,255,255,0.1) 0%, rgba(0,0,0,0.1) 100%)',
    zIndex: 1
  },
  heroContent: {
    position: 'relative',
    zIndex: 2
  },
  title: {
    fontSize: '3.5rem',
    fontWeight: '800',
    marginBottom: '1.5rem',
    letterSpacing: '-0.5px',
    textShadow: '0 2px 4px rgba(0,0,0,0.1)'
  },
  subtitle: {
    fontSize: '1.5rem',
    opacity: '0.9',
    fontWeight: '500',
    maxWidth: '800px',
    margin: '0 auto',
    lineHeight: '1.6'
  },
  section: {
    background: 'white',
    borderRadius: '1.5rem',
    padding: '3rem',
    marginBottom: '3rem',
    boxShadow: '0 10px 30px rgba(0,0,0,0.05)',
    border: '1px solid rgba(0,0,0,0.05)',
    transition: 'transform 0.3s ease, box-shadow 0.3s ease'
  },
  sectionHover: {
    transform: 'translateY(-5px)',
    boxShadow: '0 15px 35px rgba(0,0,0,0.1)'
  },
  sectionTitle: {
    color: 'var(--theme-primary)',
    fontSize: '2rem',
    fontWeight: '700',
    marginBottom: '2rem',
    paddingBottom: '1rem',
    borderBottom: '2px solid #e2e8f0',
    position: 'relative'
  },
  sectionTitleAfter: {
    content: '""',
    position: 'absolute',
    bottom: '-2px',
    left: '0',
    width: '100px',
    height: '2px',
    background: 'linear-gradient(90deg, var(--theme-accent), transparent)'
  },
  featureGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
    gap: '2rem',
    marginTop: '2rem'
  },
  featureCard: {
    background: '#f8fafc',
    padding: '2rem',
    borderRadius: '1rem',
    border: '1px solid #e2e8f0',
    transition: 'all 0.3s ease'
  },
  featureCardHover: {
    background: 'white',
    transform: 'translateY(-5px)',
    boxShadow: '0 10px 20px rgba(0,0,0,0.05)'
  },
  featureTitle: {
    fontSize: '1.25rem',
    fontWeight: '600',
    marginBottom: '1rem',
    color: 'var(--theme-primary)'
  },
  featureDescription: {
    color: '#4a5568',
    lineHeight: '1.6'
  },
  highlight: {
    background: 'linear-gradient(135deg, #e6f9f6 0%, #f0f9ff 100%)',
    padding: '2rem',
    borderRadius: '1rem',
    margin: '2rem 0',
    borderLeft: '4px solid var(--theme-accent)',
    boxShadow: '0 5px 15px var(--theme-accent-soft)'
  },
  badge: {
    display: 'inline-block',
    background: 'linear-gradient(135deg, var(--theme-accent) 0%, var(--theme-primary) 100%)',
    color: 'white',
    padding: '0.75rem 1.5rem',
    borderRadius: '2rem',
    margin: '0.5rem',
    fontWeight: '600',
    fontSize: '0.9rem',
    boxShadow: '0 4px 6px var(--theme-shadow-accent)',
    transition: 'transform 0.2s ease'
  },
  badgeHover: {
    transform: 'translateY(-2px)'
  },
  statsContainer: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
    gap: '2rem',
    margin: '3rem 0'
  },
  statCard: {
    textAlign: 'center',
    padding: '2rem',
    background: 'white',
    borderRadius: '1rem',
    boxShadow: '0 5px 15px rgba(0,0,0,0.05)'
  },
  statNumber: {
    fontSize: '2.5rem',
    fontWeight: '700',
    color: 'var(--theme-accent)',
    marginBottom: '0.5rem'
  },
  statLabel: {
    color: '#4a5568',
    fontSize: '1rem',
    fontWeight: '500'
  },
  videoGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
    gap: '2rem',
    marginTop: '2rem'
  },
  videoCard: {
    background: '#f8fafc',
    borderRadius: '1rem',
    padding: '1rem',
    border: '1px solid #e2e8f0',
    boxShadow: '0 5px 15px rgba(0,0,0,0.05)'
  },
  videoFrameWrapper: {
    position: 'relative',
    width: '100%',
    paddingTop: '56.25%',
    overflow: 'hidden',
    borderRadius: '0.75rem',
    background: 'var(--theme-primary)'
  },
  videoFrame: {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    border: 'none',
    borderRadius: '0.75rem'
  },
  heroGrid: {
    display: 'grid',
    gridTemplateColumns: '1.1fr 0.9fr',
    gap: '2rem',
    alignItems: 'center'
  },
  heroTextBlock: {
    textAlign: 'left'
  },
  heroImageCard: {
    background: 'rgba(255,255,255,0.12)',
    border: '1px solid rgba(255,255,255,0.22)',
    borderRadius: '1.5rem',
    padding: '1rem',
    backdropFilter: 'blur(8px)',
    overflow: 'hidden',
    boxShadow: '0 18px 40px var(--theme-shadow-accent)'
  },
  heroImage: {
    width: '100%',
    height: '320px',
    objectFit: 'contain',
    borderRadius: '1rem',
    background: 'rgba(255,255,255,0.92)',
    transition: 'transform 0.45s ease, filter 0.45s ease'
  },
  carouselViewport: {
    position: 'relative'
  },
  carouselControls: {
    position: 'absolute',
    inset: '0',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '0 0.85rem',
    pointerEvents: 'none'
  },
  carouselButton: {
    width: '42px',
    height: '42px',
    borderRadius: '999px',
    border: '1px solid rgba(255,255,255,0.35)',
    background: 'color-mix(in srgb, var(--theme-primary) 42%, transparent)',
    color: '#ffffff',
    fontSize: '1.4rem',
    lineHeight: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    pointerEvents: 'auto',
    backdropFilter: 'blur(8px)',
    transition: 'transform 0.25s ease, background 0.25s ease'
  },
  carouselDots: {
    display: 'flex',
    justifyContent: 'center',
    gap: '0.5rem',
    marginTop: '1rem'
  },
  carouselDot: {
    width: '9px',
    height: '9px',
    borderRadius: '999px',
    border: 'none',
    background: 'rgba(255,255,255,0.38)',
    cursor: 'pointer',
    transition: 'transform 0.25s ease, background 0.25s ease'
  },
  carouselDotActive: {
    background: '#ffffff',
    transform: 'scale(1.25)'
  },
  twoColumnGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
    gap: '2rem'
  },
  listCard: {
    background: '#f8fafc',
    padding: '2rem',
    borderRadius: '1rem',
    border: '1px solid #e2e8f0'
  },
  supportCard: {
    background: 'linear-gradient(135deg, #effcf9 0%, #f8fbff 100%)',
    padding: '2rem',
    borderRadius: '1.2rem',
    border: '1px solid var(--theme-accent-soft)'
  },
  ctaSection: {
    background: 'linear-gradient(135deg, var(--theme-hero-start) 0%, var(--theme-hero-end) 100%)',
    color: 'white',
    borderRadius: '1.5rem',
    padding: '2.5rem',
    marginBottom: '3rem',
    boxShadow: '0 16px 40px var(--theme-shadow-accent)',
    textAlign: 'center',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center'
  },
  ctaTitle: {
    fontSize: '2rem',
    fontWeight: '800',
    marginBottom: '0.9rem',
    lineHeight: '1.2'
  },
  ctaText: {
    fontSize: '1.05rem',
    lineHeight: '1.8',
    opacity: 0.94,
    marginBottom: '1.5rem',
    maxWidth: '680px'
  },
  ctaActions: {
    display: 'flex',
    gap: '1rem',
    flexWrap: 'wrap',
    justifyContent: 'center'
  },
  ctaButtonPrimary: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '0.95rem 1.5rem',
    borderRadius: '999px',
    background: '#ffffff',
    color: 'var(--theme-primary)',
    textDecoration: 'none',
    fontWeight: '700'
  },
  listTitle: {
    color: 'var(--theme-primary)',
    fontSize: '1.2rem',
    fontWeight: '700',
    marginBottom: '1rem'
  },
  list: {
    margin: 0,
    paddingLeft: '1.1rem',
    color: '#4a5568',
    lineHeight: '1.8'
  },
  specTable: {
    width: '100%',
    borderCollapse: 'collapse'
  },
  specRow: {
    borderBottom: '1px solid #e2e8f0'
  },
  specLabel: {
    width: '32%',
    padding: '1rem 1rem 1rem 0',
    verticalAlign: 'top',
    fontWeight: '700',
    color: 'var(--theme-primary)'
  },
  specValue: {
    padding: '1rem 0',
    color: '#4a5568',
    lineHeight: '1.6'
  }
});

const getResponsiveStyles = (viewportWidth) => {
  const styles = createStyles();
  const isMobile = viewportWidth < 768;
  const isTablet = viewportWidth >= 768 && viewportWidth < 1024;

  const overrides = {
    mainContainer: {
      padding: isMobile ? '3.5rem 0 1rem' : isTablet ? '6.5rem 0 1.5rem' : '2rem 0'
    },
    container: {
      padding: isMobile ? '0 1rem' : isTablet ? '0 1.5rem' : '0 2rem'
    },
    hero: {
      padding: isMobile ? '2.5rem 1rem' : isTablet ? '3.5rem 1.5rem' : '5rem 2rem',
      borderRadius: isMobile ? '1rem' : isTablet ? '1.5rem' : '2rem',
      marginBottom: isMobile ? '2rem' : isTablet ? '3rem' : '4rem'
    },
    title: {
      fontSize: isMobile ? '2rem' : isTablet ? '2.75rem' : '3.5rem',
      marginBottom: isMobile ? '1rem' : '1.5rem',
      letterSpacing: '0',
      lineHeight: isMobile ? '1.2' : '1.1'
    },
    subtitle: {
      fontSize: isMobile ? '1rem' : isTablet ? '1.2rem' : '1.5rem',
      lineHeight: isMobile ? '1.7' : '1.6'
    },
    section: {
      borderRadius: isMobile ? '1rem' : isTablet ? '1.25rem' : '1.5rem',
      padding: isMobile ? '1.25rem' : isTablet ? '2rem' : '3rem',
      marginBottom: isMobile ? '1.5rem' : isTablet ? '2rem' : '3rem'
    },
    sectionTitle: {
      fontSize: isMobile ? '1.4rem' : isTablet ? '1.7rem' : '2rem',
      marginBottom: isMobile ? '1.25rem' : '2rem',
      paddingBottom: isMobile ? '0.75rem' : '1rem'
    },
    featureGrid: {
      gridTemplateColumns: isMobile ? '1fr' : isTablet ? 'repeat(auto-fit, minmax(240px, 1fr))' : 'repeat(auto-fit, minmax(300px, 1fr))',
      gap: isMobile ? '1rem' : isTablet ? '1.5rem' : '2rem',
      marginTop: isMobile ? '1rem' : '2rem'
    },
    featureCard: {
      padding: isMobile ? '1rem' : isTablet ? '1.5rem' : '2rem'
    },
    featureTitle: {
      fontSize: isMobile ? '1.05rem' : isTablet ? '1.15rem' : '1.25rem',
      marginBottom: isMobile ? '0.75rem' : '1rem'
    },
    highlight: {
      padding: isMobile ? '1rem' : isTablet ? '1.5rem' : '2rem',
      margin: isMobile ? '1rem 0' : '2rem 0'
    },
    statsContainer: {
      gridTemplateColumns: isMobile ? '1fr 1fr' : isTablet ? 'repeat(auto-fit, minmax(180px, 1fr))' : 'repeat(auto-fit, minmax(200px, 1fr))',
      gap: isMobile ? '1rem' : isTablet ? '1.5rem' : '2rem',
      margin: isMobile ? '1.5rem 0' : '3rem 0'
    },
    statCard: {
      padding: isMobile ? '1rem' : isTablet ? '1.5rem' : '2rem'
    },
    statNumber: {
      fontSize: isMobile ? '1.5rem' : isTablet ? '2rem' : '2.5rem'
    },
    statLabel: {
      fontSize: isMobile ? '0.9rem' : '1rem'
    },
    videoGrid: {
      gridTemplateColumns: isMobile ? '1fr' : isTablet ? '1fr' : 'repeat(auto-fit, minmax(320px, 1fr))',
      gap: isMobile ? '1rem' : '2rem',
      marginTop: isMobile ? '1rem' : '2rem'
    },
    videoCard: {
      padding: isMobile ? '0.75rem' : '1rem'
    },
    heroGrid: {
      gridTemplateColumns: isMobile ? '1fr' : isTablet ? '1fr' : '1.1fr 0.9fr',
      gap: isMobile ? '1rem' : '2rem'
    },
    heroTextBlock: {
      textAlign: isMobile ? 'center' : 'left'
    },
    heroImageCard: {
      borderRadius: isMobile ? '1rem' : '1.5rem',
      padding: isMobile ? '0.75rem' : '1rem'
    },
    heroImage: {
      height: isMobile ? '220px' : isTablet ? '280px' : '320px'
    },
    carouselButton: {
      width: isMobile ? '36px' : '42px',
      height: isMobile ? '36px' : '42px',
      fontSize: isMobile ? '1.2rem' : '1.4rem'
    },
    twoColumnGrid: {
      gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, minmax(0, 1fr))',
      gap: isMobile ? '1rem' : '2rem'
    },
    listCard: {
      padding: isMobile ? '1rem' : isTablet ? '1.5rem' : '2rem'
    },
    supportCard: {
      padding: isMobile ? '1rem' : isTablet ? '1.5rem' : '2rem'
    },
    ctaSection: {
      borderRadius: isMobile ? '1rem' : '1.5rem',
      padding: isMobile ? '1.25rem' : isTablet ? '2rem' : '2.5rem',
      marginBottom: isMobile ? '1.5rem' : '3rem'
    },
    ctaTitle: {
      fontSize: isMobile ? '1.35rem' : isTablet ? '1.7rem' : '2rem'
    },
    ctaText: {
      fontSize: isMobile ? '0.98rem' : '1.05rem',
      marginBottom: isMobile ? '1rem' : '1.5rem'
    },
    ctaActions: {
      gap: isMobile ? '0.75rem' : '1rem'
    },
    ctaButtonPrimary: {
      width: isMobile ? '100%' : 'auto',
      padding: isMobile ? '0.9rem 1.2rem' : '0.95rem 1.5rem'
    },
    listTitle: {
      fontSize: isMobile ? '1.05rem' : '1.2rem'
    },
    specLabel: {
      width: isMobile ? '38%' : '32%',
      padding: isMobile ? '0.75rem 0.75rem 0.75rem 0' : '1rem 1rem 1rem 0'
    },
    specValue: {
      padding: isMobile ? '0.75rem 0' : '1rem 0'
    }
  };

  return Object.keys(styles).reduce((accumulator, key) => {
    accumulator[key] = {
      ...styles[key],
      ...(overrides[key] || {})
    };
    return accumulator;
  }, {});
};

const renderFeatureGrid = (items, styles) => (
  <div style={styles.featureGrid}>
    {items.map((item, index) => (
      <div key={index} style={styles.featureCard}>
        <h3 style={styles.featureTitle}>{item.title}</h3>
        <p style={styles.featureDescription}>{item.description}</p>
      </div>
    ))}
  </div>
);

const renderStatsGrid = (items, styles) => (
  <div style={styles.statsContainer}>
    {items.map((item, index) => (
      <div key={index} style={styles.statCard}>
        <div style={styles.statNumber}>{item.value}</div>
        <div style={styles.statLabel}>{item.label}</div>
      </div>
    ))}
  </div>
);

const renderVideoGrid = (items, styles) => (
  <div style={styles.videoGrid}>
    {items.map((item, index) => (
      <div key={index} style={styles.videoCard}>
        <div style={styles.videoFrameWrapper}>
          <iframe
            style={styles.videoFrame}
            src={item.url}
            title={item.title}
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            referrerPolicy="strict-origin-when-cross-origin"
            allowFullScreen
          />
        </div>
        <h3 style={{ ...styles.featureTitle, marginTop: '1rem', marginBottom: '0.5rem' }}>{item.title}</h3>
        <p style={styles.featureDescription}>{item.description}</p>
      </div>
    ))}
  </div>
);

const renderProductContent = ({ heroTitle, subtitle, sections, styles }) => (
  <div style={styles.mainContainer}>
    <div style={styles.container}>
      <div style={styles.hero}>
        <div style={styles.heroOverlay}></div>
        <div style={styles.heroContent}>
          <h1 style={styles.title}>{heroTitle}</h1>
          <p style={styles.subtitle}>{subtitle}</p>
        </div>
      </div>

      {sections.map((section, index) => (
        <div key={index} style={styles.section}>
          <h2 style={styles.sectionTitle}>{section.title}</h2>
          {section.paragraphs ? section.paragraphs.map((paragraph, paragraphIndex) => (
            <p key={paragraphIndex} style={styles.featureDescription}>{paragraph}</p>
          )) : null}
          {section.type === 'stats' ? renderStatsGrid(section.items, styles) : null}
          {section.type === 'videos' ? renderVideoGrid(section.items, styles) : null}
          {section.type === 'grid' && section.highlight ? (
            <div style={styles.highlight}>
              {renderFeatureGrid(section.items, styles)}
            </div>
          ) : null}
          {section.type === 'grid' && !section.highlight ? renderFeatureGrid(section.items, styles) : null}
        </div>
      ))}
    </div>
  </div>
);

const HardwareProductContent = ({ product, styles }) => {
  const galleryImages = useMemo(
    () => (product.images && product.images.length ? product.images : []),
    [product.images]
  );
  const [activeIndex, setActiveIndex] = useState(0);
  const [isCarouselPaused, setIsCarouselPaused] = useState(false);

  useEffect(() => {
    setActiveIndex(0);
  }, [product.slug, galleryImages]);

  useEffect(() => {
    if (galleryImages.length <= 1 || isCarouselPaused) {
      return undefined;
    }

    const intervalId = window.setInterval(() => {
      setActiveIndex((currentIndex) => (currentIndex + 1) % galleryImages.length);
    }, 2000);

    return () => {
      window.clearInterval(intervalId);
    };
  }, [galleryImages, isCarouselPaused]);

  const activeImage = galleryImages[activeIndex] || '';
  const showPreviousImage = () => {
    setActiveIndex((currentIndex) =>
      currentIndex === 0 ? galleryImages.length - 1 : currentIndex - 1
    );
  };
  const showNextImage = () => {
    setActiveIndex((currentIndex) => (currentIndex + 1) % galleryImages.length);
  };

  return (
    <div style={styles.mainContainer}>
      <div style={styles.container}>
        <div style={styles.hero}>
          <div style={styles.heroOverlay}></div>
          <div style={{ ...styles.heroContent, ...styles.heroGrid }}>
            <div style={styles.heroTextBlock}>
              <h1 style={styles.title}>{product.label}</h1>
              <p style={styles.subtitle}>{product.shortDescription}</p>
            </div>
            <div
              style={styles.heroImageCard}
              onMouseEnter={(event) => {
                setIsCarouselPaused(true);
                const image = event.currentTarget.querySelector('[data-main-product-image]');
                if (image) {
                  image.style.transform = 'scale(1.12)';
                  image.style.filter = 'saturate(1.05)';
                }
              }}
              onMouseLeave={(event) => {
                setIsCarouselPaused(false);
                const image = event.currentTarget.querySelector('[data-main-product-image]');
                if (image) {
                  image.style.transform = 'scale(1)';
                  image.style.filter = 'saturate(1)';
                }
              }}
            >
              <div style={styles.carouselViewport}>
                <img
                  src={activeImage}
                  alt={product.label}
                  style={styles.heroImage}
                  data-main-product-image
                />
                {galleryImages.length > 1 ? (
                  <div style={styles.carouselControls}>
                    <button
                      type="button"
                      style={styles.carouselButton}
                      onClick={showPreviousImage}
                      aria-label="Show previous product image"
                    >
                      &#8249;
                    </button>
                    <button
                      type="button"
                      style={styles.carouselButton}
                      onClick={showNextImage}
                      aria-label="Show next product image"
                    >
                      &#8250;
                    </button>
                  </div>
                ) : null}
              </div>
              {galleryImages.length > 1 ? (
                <div style={styles.carouselDots}>
                  {galleryImages.map((image, index) => (
                    <button
                      key={`${product.slug}-${index}`}
                      type="button"
                      style={{
                        ...styles.carouselDot,
                        ...(activeIndex === index ? styles.carouselDotActive : {})
                      }}
                      onClick={() => setActiveIndex(index)}
                      aria-label={`Show ${product.label} image ${index + 1}`}
                    />
                  ))}
                </div>
              ) : null}
            </div>
          </div>
        </div>

        <div style={styles.section}>
          <h2 style={styles.sectionTitle}>Overview</h2>
          <p style={styles.featureDescription}>{product.overview}</p>
        </div>

        <div style={styles.section}>
          <h2 style={styles.sectionTitle}>Product Highlights</h2>
          <div style={styles.twoColumnGrid}>
            <div style={styles.listCard}>
              <h3 style={styles.listTitle}>Key Features</h3>
              <ul style={styles.list}>
                {product.features.map((feature) => (
                  <li key={feature}>{feature}</li>
                ))}
              </ul>
            </div>
            <div style={styles.listCard}>
              <h3 style={styles.listTitle}>Business Benefits</h3>
              <ul style={styles.list}>
                {product.benefits.map((benefit) => (
                  <li key={benefit}>{benefit}</li>
                ))}
              </ul>
            </div>
          </div>
        </div>

        <div style={styles.section}>
          <h2 style={styles.sectionTitle}>Use Cases</h2>
          <div style={styles.featureGrid}>
            {product.useCases.map((useCase) => (
              <div key={useCase} style={styles.featureCard}>
                <h3 style={styles.featureTitle}>{useCase}</h3>
                <p style={styles.featureDescription}>
                  This deployment pattern is well suited for organizations that need dependable hardware backed by configuration and rollout support.
                </p>
              </div>
            ))}
          </div>
        </div>

        <div style={styles.section}>
          <h2 style={styles.sectionTitle}>Integrations and Deployment</h2>
          <div style={styles.twoColumnGrid}>
            <div style={styles.listCard}>
              <h3 style={styles.listTitle}>Common Integrations</h3>
              <ul style={styles.list}>
                {product.integrations.map((integration) => (
                  <li key={integration}>{integration}</li>
                ))}
              </ul>
            </div>
            <div style={styles.supportCard}>
              <h3 style={styles.listTitle}>Deployment Approach</h3>
              <p style={styles.featureDescription}>{product.deployment}</p>
            </div>
          </div>
        </div>

        <div style={styles.section}>
          <h2 style={styles.sectionTitle}>Technical Specifications</h2>
          <table style={styles.specTable}>
            <tbody>
              {product.specifications.map((specification) => (
                <tr key={specification.label} style={styles.specRow}>
                  <td style={styles.specLabel}>{specification.label}</td>
                  <td style={styles.specValue}>{specification.value}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div style={styles.ctaSection}>
          <h2 style={styles.ctaTitle}>{product.ctaTitle}</h2>
          <p style={styles.ctaText}>{product.ctaText}</p>
          <div style={styles.ctaActions}>
            <a href="/contact" style={styles.ctaButtonPrimary}>Request a Consultation</a>
          </div>
        </div>
      </div>
    </div>
  );
};

const getProductDetails = (styles) => ({
  'attendance-management-system': {
    title: 'Attendance Management System',
    content: (
      <div style={styles.mainContainer}>
        <div style={styles.container}>
          <div style={styles.hero}>
            <div style={styles.heroOverlay}></div>
            <div style={styles.heroContent}>
              <h1 style={styles.title}>E-Attendance System</h1>
              <p style={styles.subtitle}>Transform your organization's attendance management with our cutting-edge, automated solution that ensures accuracy, efficiency, and transparency.</p>
            </div>
          </div>

          <div style={styles.section}>
            <h2 style={styles.sectionTitle}>Why Choose Our System?</h2>
            <div style={styles.statsContainer}>
              <div style={styles.statCard}>
                <div style={styles.statNumber}>99.9%</div>
                <div style={styles.statLabel}>Accuracy Rate</div>
              </div>
              <div style={styles.statCard}>
                <div style={styles.statNumber}>50%</div>
                <div style={styles.statLabel}>Time Saved</div>
              </div>
              <div style={styles.statCard}>
                <div style={styles.statNumber}>100%</div>
                <div style={styles.statLabel}>Compliance</div>
              </div>
              <div style={styles.statCard}>
                <div style={styles.statNumber}>24/7</div>
                <div style={styles.statLabel}>Support</div>
              </div>
            </div>
          </div>

          <div style={styles.section}>
            <h2 style={styles.sectionTitle}>Key Features</h2>
            <div style={styles.featureGrid}>
              <div style={styles.featureCard}>
                <h3 style={styles.featureTitle}>Mobile Access</h3>
                <p style={styles.featureDescription}>Access attendance data anytime, anywhere through our mobile-friendly interface.</p>
              </div>
              <div style={styles.featureCard}>
                <h3 style={styles.featureTitle}>Secure Authentication</h3>
                <p style={styles.featureDescription}>Advanced biometric and multi-factor authentication for enhanced security.</p>
              </div>
              <div style={styles.featureCard}>
                <h3 style={styles.featureTitle}>Real-time Analytics</h3>
                <p style={styles.featureDescription}>Comprehensive reporting and analytics for better decision-making.</p>
              </div>
              <div style={styles.featureCard}>
                <h3 style={styles.featureTitle}>Automated Workflows</h3>
                <p style={styles.featureDescription}>Streamlined processes that reduce manual work and improve efficiency.</p>
              </div>
            </div>
          </div>

          <div style={styles.section}>
            <h2 style={styles.sectionTitle}>System Modules</h2>
            <div style={styles.highlight}>
              <div style={styles.featureGrid}>
                <div style={styles.featureCard}>
                  <h3 style={styles.featureTitle}>Dashboard</h3>
                  <p style={styles.featureDescription}>Comprehensive overview of attendance data with customizable widgets.</p>
                </div>
                <div style={styles.featureCard}>
                  <h3 style={styles.featureTitle}>Employee Management</h3>
                  <p style={styles.featureDescription}>Complete employee profile management and access control system.</p>
                </div>
                <div style={styles.featureCard}>
                  <h3 style={styles.featureTitle}>Attendance Tracking</h3>
                  <p style={styles.featureDescription}>Real-time monitoring and reporting of attendance records.</p>
                </div>
                <div style={styles.featureCard}>
                  <h3 style={styles.featureTitle}>Leave Management</h3>
                  <p style={styles.featureDescription}>Automated leave requests, approvals, and tracking system.</p>
                </div>
              </div>
            </div>
          </div>

          <div style={styles.section}>
            <h2 style={styles.sectionTitle}>Benefits</h2>
            <div style={styles.featureGrid}>
              <div style={styles.featureCard}>
                <h3 style={styles.featureTitle}>Cost Reduction</h3>
                <p style={styles.featureDescription}>Minimize administrative costs and improve operational efficiency.</p>
              </div>
              <div style={styles.featureCard}>
                <h3 style={styles.featureTitle}>Enhanced Security</h3>
                <p style={styles.featureDescription}>Protect sensitive data with advanced security measures.</p>
              </div>
              <div style={styles.featureCard}>
                <h3 style={styles.featureTitle}>Improved Compliance</h3>
                <p style={styles.featureDescription}>Ensure adherence to organizational policies and regulations.</p>
              </div>
              <div style={styles.featureCard}>
                <h3 style={styles.featureTitle}>Better Decision Making</h3>
                <p style={styles.featureDescription}>Access to real-time data for informed decision-making.</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  },
  'vehicle-tracking-system': {
    title: 'Vehicle Tracking System',
    content: (
      <div style={styles.mainContainer}>
        <div style={styles.container}>
          <div style={styles.hero}>
            <div style={styles.heroOverlay}></div>
            <div style={styles.heroContent}>
              <h1 style={styles.title}>Vehicle Tracking System</h1>
              <p style={styles.subtitle}>Transform your fleet management with our comprehensive GPS-based tracking solution that ensures efficiency, security, and real-time monitoring.</p>
            </div>
          </div>

          <div style={styles.section}>
            <h2 style={styles.sectionTitle}>Project Scope</h2>
            <div style={styles.featureGrid}>
              <div style={styles.featureCard}>
                <h3 style={styles.featureTitle}>System Requirements</h3>
                <p style={styles.featureDescription}>Comprehensive tracking, route optimization, and advanced reporting capabilities.</p>
              </div>
              <div style={styles.featureCard}>
                <h3 style={styles.featureTitle}>Hardware & Software</h3>
                <p style={styles.featureDescription}>GPS devices, communication modules, and intuitive tracking software.</p>
              </div>
              <div style={styles.featureCard}>
                <h3 style={styles.featureTitle}>Integration & Testing</h3>
                <p style={styles.featureDescription}>Seamless integration of components with thorough testing and validation.</p>
              </div>
              <div style={styles.featureCard}>
                <h3 style={styles.featureTitle}>Training & Support</h3>
                <p style={styles.featureDescription}>Comprehensive user training and ongoing technical support.</p>
              </div>
            </div>
          </div>

          <div style={styles.section}>
            <h2 style={styles.sectionTitle}>Main Modules</h2>
            <div style={styles.highlight}>
              <div style={styles.featureGrid}>
                <div style={styles.featureCard}>
                  <h3 style={styles.featureTitle}>Vehicle Management</h3>
                  <p style={styles.featureDescription}>Complete vehicle information and tracking management.</p>
                </div>
                <div style={styles.featureCard}>
                  <h3 style={styles.featureTitle}>Employee Information</h3>
                  <p style={styles.featureDescription}>Driver details, department, and designation management.</p>
                </div>
                <div style={styles.featureCard}>
                  <h3 style={styles.featureTitle}>Vehicle Tracking</h3>
                  <p style={styles.featureDescription}>Real-time GPS tracking and location monitoring.</p>
                </div>
                <div style={styles.featureCard}>
                  <h3 style={styles.featureTitle}>Fuel Management</h3>
                  <p style={styles.featureDescription}>Track and optimize fuel consumption and costs.</p>
                </div>
              </div>
            </div>
          </div>

          <div style={styles.section}>
            <h2 style={styles.sectionTitle}>Key Features</h2>
            <div style={styles.statsContainer}>
              <div style={styles.statCard}>
                <div style={styles.statNumber}>24/7</div>
                <div style={styles.statLabel}>Real-time Tracking</div>
              </div>
              <div style={styles.statCard}>
                <div style={styles.statNumber}>100%</div>
                <div style={styles.statLabel}>Geofencing</div>
              </div>
              <div style={styles.statCard}>
                <div style={styles.statNumber}>✓</div>
                <div style={styles.statLabel}>Custom Reports</div>
              </div>
              <div style={styles.statCard}>
                <div style={styles.statNumber}>✓</div>
                <div style={styles.statLabel}>Smart Alerts</div>
              </div>
            </div>
          </div>

          <div style={styles.section}>
            <h2 style={styles.sectionTitle}>Benefits</h2>
            <div style={styles.featureGrid}>
              <div style={styles.featureCard}>
                <h3 style={styles.featureTitle}>Improved Fleet Management</h3>
                <p style={styles.featureDescription}>Optimize routes and reduce operational costs.</p>
              </div>
              <div style={styles.featureCard}>
                <h3 style={styles.featureTitle}>Enhanced Security</h3>
                <p style={styles.featureDescription}>Prevent theft and unauthorized vehicle use.</p>
              </div>
              <div style={styles.featureCard}>
                <h3 style={styles.featureTitle}>Increased Productivity</h3>
                <p style={styles.featureDescription}>Better resource allocation and time management.</p>
              </div>
              <div style={styles.featureCard}>
                <h3 style={styles.featureTitle}>Data Analytics</h3>
                <p style={styles.featureDescription}>Valuable insights for operational optimization.</p>
              </div>
            </div>
          </div>

          <div style={styles.section}>
            <h2 style={styles.sectionTitle}>Implementation Timeline</h2>
            <div style={styles.featureGrid}>
              <div style={styles.featureCard}>
                <h3 style={styles.featureTitle}>Hardware Installation</h3>
                <p style={styles.featureDescription}>GPS device installation in vehicles.</p>
              </div>
              <div style={styles.featureCard}>
                <h3 style={styles.featureTitle}>Software Configuration</h3>
                <p style={styles.featureDescription}>System setup and customization.</p>
              </div>
              <div style={styles.featureCard}>
                <h3 style={styles.featureTitle}>User Training</h3>
                <p style={styles.featureDescription}>Comprehensive training sessions.</p>
              </div>
              <div style={styles.featureCard}>
                <h3 style={styles.featureTitle}>System Testing</h3>
                <p style={styles.featureDescription}>Thorough validation and testing.</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  },
  'vehicle-workshop-management-system': {
    title: 'Vehicle Workshop Management System',
    content: (
      <div style={styles.mainContainer}>
        <div style={styles.container}>
          <div style={styles.hero}>
            <div style={styles.heroOverlay}></div>
            <div style={styles.heroContent}>
              <h1 style={styles.title}>Vehicle Workshop Management System</h1>
              <p style={styles.subtitle}>Transform your workshop operations with our comprehensive management solution that ensures efficiency, accuracy, and streamlined service delivery.</p>
            </div>
          </div>

          <div style={styles.section}>
            <h2 style={styles.sectionTitle}>Project Scope</h2>
            <div style={styles.featureGrid}>
              <div style={styles.featureCard}>
                <h3 style={styles.featureTitle}>Maintenance & Repair</h3>
                <p style={styles.featureDescription}>Comprehensive vehicle maintenance and repair tracking with regular inspections.</p>
              </div>
              <div style={styles.featureCard}>
                <h3 style={styles.featureTitle}>Inventory Management</h3>
                <p style={styles.featureDescription}>Efficient tracking of parts, tools, and supplies with automated reordering.</p>
              </div>
              <div style={styles.featureCard}>
                <h3 style={styles.featureTitle}>Job Management</h3>
                <p style={styles.featureDescription}>Complete job card creation, tracking, and invoice generation system.</p>
              </div>
              <div style={styles.featureCard}>
                <h3 style={styles.featureTitle}>Employee Management</h3>
                <p style={styles.featureDescription}>Comprehensive employee information and work schedule management.</p>
              </div>
            </div>
          </div>

          <div style={styles.section}>
            <h2 style={styles.sectionTitle}>Main Modules</h2>
            <div style={styles.highlight}>
              <div style={styles.featureGrid}>
                <div style={styles.featureCard}>
                  <h3 style={styles.featureTitle}>Vehicle Details</h3>
                  <p style={styles.featureDescription}>Complete vehicle information and service history tracking.</p>
                </div>
                <div style={styles.featureCard}>
                  <h3 style={styles.featureTitle}>Item Management</h3>
                  <p style={styles.featureDescription}>Inventory tracking and parts management system.</p>
                </div>
                <div style={styles.featureCard}>
                  <h3 style={styles.featureTitle}>Employee Management</h3>
                  <p style={styles.featureDescription}>Staff details, roles, and department management.</p>
                </div>
                <div style={styles.featureCard}>
                  <h3 style={styles.featureTitle}>Vendor Management</h3>
                  <p style={styles.featureDescription}>Supplier information and procurement tracking.</p>
                </div>
              </div>
            </div>
          </div>

          <div style={styles.section}>
            <h2 style={styles.sectionTitle}>Key Features</h2>
            <div style={styles.statsContainer}>
              <div style={styles.statCard}>
                <div style={styles.statNumber}>✓</div>
                <div style={styles.statLabel}>Work Order Management</div>
              </div>
              <div style={styles.statCard}>
                <div style={styles.statNumber}>✓</div>
                <div style={styles.statLabel}>Inventory Tracking</div>
              </div>
              <div style={styles.statCard}>
                <div style={styles.statNumber}>✓</div>
                <div style={styles.statLabel}>Automated Invoicing</div>
              </div>
              <div style={styles.statCard}>
                <div style={styles.statNumber}>✓</div>
                <div style={styles.statLabel}>Real-time Analytics</div>
              </div>
            </div>
          </div>

          <div style={styles.section}>
            <h2 style={styles.sectionTitle}>Benefits</h2>
            <div style={styles.featureGrid}>
              <div style={styles.featureCard}>
                <h3 style={styles.featureTitle}>Streamlined Operations</h3>
                <p style={styles.featureDescription}>Automated workflows reduce errors and improve efficiency.</p>
              </div>
              <div style={styles.featureCard}>
                <h3 style={styles.featureTitle}>Improved Service</h3>
                <p style={styles.featureDescription}>Real-time status updates and automated notifications.</p>
              </div>
              <div style={styles.featureCard}>
                <h3 style={styles.featureTitle}>Better Resource Utilization</h3>
                <p style={styles.featureDescription}>Optimal job assignment and reduced idle time.</p>
              </div>
              <div style={styles.featureCard}>
                <h3 style={styles.featureTitle}>Enhanced Inventory Control</h3>
                <p style={styles.featureDescription}>Efficient stock management and automated reordering.</p>
              </div>
            </div>
          </div>

          <div style={styles.section}>
            <h2 style={styles.sectionTitle}>Implementation Process</h2>
            <div style={styles.featureGrid}>
              <div style={styles.featureCard}>
                <h3 style={styles.featureTitle}>Planning</h3>
                <p style={styles.featureDescription}>Detailed project planning and resource allocation.</p>
              </div>
              <div style={styles.featureCard}>
                <h3 style={styles.featureTitle}>Configuration</h3>
                <p style={styles.featureDescription}>System setup and user account management.</p>
              </div>
              <div style={styles.featureCard}>
                <h3 style={styles.featureTitle}>Training</h3>
                <p style={styles.featureDescription}>Comprehensive user training and support.</p>
              </div>
              <div style={styles.featureCard}>
                <h3 style={styles.featureTitle}>Deployment</h3>
                <p style={styles.featureDescription}>System deployment and ongoing maintenance.</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  },
  'face-recognition-ai': {
    title: 'Face Recognition and Detection Using AI',
    content: renderProductContent({
      styles,
      heroTitle: 'Face Recognition and Detection Using AI',
      subtitle: 'Deploy intelligent facial detection and identity verification workflows that strengthen security, automate attendance, and improve user authentication across enterprise environments.',
      sections: [
        {
          title: 'Solution Overview',
          paragraphs: [
            'This AI-powered platform detects, identifies, and verifies faces from live camera feeds, uploaded images, and integrated devices. It helps organizations automate identity-driven processes while maintaining speed, consistency, and auditability.',
            'The solution can be adapted for attendance marking, access control, visitor management, exam monitoring, and secure authentication use cases where real-time response and accurate matching are essential.'
          ]
        },
        {
          title: 'Core Capabilities',
          type: 'grid',
          items: [
            { title: 'Face Detection', description: 'Identify faces in real time from CCTV feeds, webcam streams, and captured photos.' },
            { title: 'Recognition Engine', description: 'Match faces against enrolled profiles using AI models for quick and reliable verification.' },
            { title: 'Liveness Validation', description: 'Reduce spoofing risks with checks designed to distinguish real users from static images.' },
            { title: 'Alert Automation', description: 'Trigger notifications for unauthorized access, unknown visitors, or exception-based events.' }
          ]
        },
        {
          title: 'Deployment Modules',
          type: 'grid',
          highlight: true,
          items: [
            { title: 'Enrollment Management', description: 'Create and maintain profile records with images, metadata, and role mapping.' },
            { title: 'Device Integration', description: 'Connect IP cameras, biometric devices, and attendance terminals into one workflow.' },
            { title: 'Monitoring Dashboard', description: 'Review recognition events, logs, and live monitoring status from a centralized console.' },
            { title: 'Reports and Audit Trail', description: 'Generate searchable logs for compliance, investigations, and performance review.' }
          ]
        },
        {
          title: 'Business Benefits',
          type: 'stats',
          items: [
            { value: '24/7', label: 'Automated Monitoring' },
            { value: 'AI', label: 'Recognition Engine' },
            { value: 'Live', label: 'Alert Processing' },
            { value: 'Secure', label: 'Identity Verification' }
          ]
        },
        {
          title: 'Ideal Use Cases',
          type: 'grid',
          items: [
            { title: 'Attendance Automation', description: 'Remove proxy attendance and streamline staff or student check-in processes.' },
            { title: 'Access Control', description: 'Permit only authorized users to enter restricted rooms, campuses, or facilities.' },
            { title: 'Visitor Verification', description: 'Track visitor movement and validate identities at reception or gate entry points.' },
            { title: 'Exam and Security Operations', description: 'Support proctoring, monitoring, and identity validation in high-trust environments.' }
          ]
        }
      ]
    })
  },
  'e-scrap': {
    title: 'Landfill Automation System',
    content: (
      <div style={styles.mainContainer}>
        <div style={styles.container}>
          <div style={styles.hero}>
            <div style={styles.heroOverlay}></div>
            <div style={styles.heroContent}>
              <h1 style={styles.title}>Landfill Automation System</h1>
              <p style={styles.subtitle}>Transform your waste management operations with our advanced RFID-based weighing and tracking solution that ensures accuracy, efficiency, and environmental compliance.</p>
            </div>
          </div>

          <div style={styles.section}>
            <h2 style={styles.sectionTitle}>Project Scope</h2>
            <div style={styles.featureGrid}>
              <div style={styles.featureCard}>
                <h3 style={styles.featureTitle}>Vehicle Weighing</h3>
                <p style={styles.featureDescription}>Accurate measurement of waste-loaded vehicles using RFID and weighing systems.</p>
              </div>
              <div style={styles.featureCard}>
                <h3 style={styles.featureTitle}>Data Management</h3>
                <p style={styles.featureDescription}>Comprehensive data collection and analysis for waste volumes and composition.</p>
              </div>
              <div style={styles.featureCard}>
                <h3 style={styles.featureTitle}>System Integration</h3>
                <p style={styles.featureDescription}>Seamless integration with existing weighing systems and waste management processes.</p>
              </div>
              <div style={styles.featureCard}>
                <h3 style={styles.featureTitle}>Quality Assurance</h3>
                <p style={styles.featureDescription}>Regular calibration and testing to ensure accurate measurements and reliable operation.</p>
              </div>
            </div>
          </div>

          <div style={styles.section}>
            <h2 style={styles.sectionTitle}>Key Features</h2>
            <div style={styles.highlight}>
              <div style={styles.featureGrid}>
                <div style={styles.featureCard}>
                  <h3 style={styles.featureTitle}>RFID Integration</h3>
                  <p style={styles.featureDescription}>Vehicle identification and tracking using RFID technology.</p>
                </div>
                <div style={styles.featureCard}>
                  <h3 style={styles.featureTitle}>Solar-Powered Cameras</h3>
                  <p style={styles.featureDescription}>GSM cameras with 4G connectivity for remote monitoring.</p>
                </div>
                <div style={styles.featureCard}>
                  <h3 style={styles.featureTitle}>Real-time Monitoring</h3>
                  <p style={styles.featureDescription}>Live tracking of vehicle movements and waste volumes.</p>
                </div>
                <div style={styles.featureCard}>
                  <h3 style={styles.featureTitle}>Automated Reporting</h3>
                  <p style={styles.featureDescription}>Comprehensive reports on waste volumes and processing operations.</p>
                </div>
              </div>
            </div>
          </div>

          <div style={styles.section}>
            <h2 style={styles.sectionTitle}>Benefits</h2>
            <div style={styles.statsContainer}>
              <div style={styles.statCard}>
                <div style={styles.statNumber}>✓</div>
                <div style={styles.statLabel}>Accurate Measurement</div>
              </div>
              <div style={styles.statCard}>
                <div style={styles.statNumber}>✓</div>
                <div style={styles.statLabel}>Cost Savings</div>
              </div>
              <div style={styles.statCard}>
                <div style={styles.statNumber}>✓</div>
                <div style={styles.statLabel}>Regulatory Compliance</div>
              </div>
              <div style={styles.statCard}>
                <div style={styles.statNumber}>✓</div>
                <div style={styles.statLabel}>Environmental Impact</div>
              </div>
            </div>
          </div>

          <div style={styles.section}>
            <h2 style={styles.sectionTitle}>Implementation Timeline</h2>
            <div style={styles.featureGrid}>
              <div style={styles.featureCard}>
                <h3 style={styles.featureTitle}>Phase 1</h3>
                <p style={styles.featureDescription}>Project planning and design (2 weeks)</p>
              </div>
              <div style={styles.featureCard}>
                <h3 style={styles.featureTitle}>Phase 2</h3>
                <p style={styles.featureDescription}>Prototype development and testing (3 weeks)</p>
              </div>
              <div style={styles.featureCard}>
                <h3 style={styles.featureTitle}>Phase 3</h3>
                <p style={styles.featureDescription}>Finalization and documentation (2 weeks)</p>
              </div>
              <div style={styles.featureCard}>
                <h3 style={styles.featureTitle}>Deployment</h3>
                <p style={styles.featureDescription}>System installation and user training</p>
              </div>
            </div>
          </div>

          <div style={styles.section}>
            <h2 style={styles.sectionTitle}>Technical Components</h2>
            <div style={styles.featureGrid}>
              <div style={styles.featureCard}>
                <h3 style={styles.featureTitle}>Hardware</h3>
                <p style={styles.featureDescription}>Weighbridge, RFID readers, solar cameras, and control systems.</p>
              </div>
              <div style={styles.featureCard}>
                <h3 style={styles.featureTitle}>Software</h3>
                <p style={styles.featureDescription}>Integration software, data management system, and reporting tools.</p>
              </div>
              <div style={styles.featureCard}>
                <h3 style={styles.featureTitle}>Connectivity</h3>
                <p style={styles.featureDescription}>4G SIM cards, GSM cameras, and cloud-based data storage.</p>
              </div>
              <div style={styles.featureCard}>
                <h3 style={styles.featureTitle}>Support</h3>
                <p style={styles.featureDescription}>One year of essential services and technical support.</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  },
  'inventory-management-system': {
    title: 'Inventory Management System',
    content: renderProductContent({
      styles,
      heroTitle: 'Inventory Management System',
      subtitle: 'Gain complete control over stock movement, warehouse visibility, and material planning with a centralized inventory platform built for accuracy and operational speed.',
      sections: [
        {
          title: 'Project Overview',
          paragraphs: [
            'Our Inventory Management System helps organizations monitor stock across warehouses, stores, and departments from a single platform. It reduces stock-outs, prevents overstocking, and improves decision-making with real-time inventory data.',
            'The solution supports procurement, inward and outward movement, stock reconciliation, and location-based inventory tracking for operations that require consistent material availability.'
          ]
        },
        {
          title: 'Key Features',
          type: 'grid',
          items: [
            { title: 'Stock Visibility', description: 'Track item quantities, locations, availability, and movement status in real time.' },
            { title: 'Purchase and Issue Control', description: 'Manage purchase entries, stock issues, returns, and approvals with full traceability.' },
            { title: 'Reorder Management', description: 'Set reorder levels and receive alerts before critical items run low.' },
            { title: 'Barcode and GIS Support', description: 'Improve accuracy using barcode workflows and map inventory across operational locations.' }
          ]
        },
        {
          title: 'Functional Modules',
          type: 'grid',
          highlight: true,
          items: [
            { title: 'Item Master', description: 'Define categories, units, item codes, vendor links, and stock parameters.' },
            { title: 'Warehouse Management', description: 'Monitor warehouse-wise receipts, transfers, and storage utilization.' },
            { title: 'Stock Transactions', description: 'Record inward, outward, transfer, adjustment, and damaged stock activity.' },
            { title: 'Reports and Analytics', description: 'Access consumption trends, aging reports, and fast-moving versus slow-moving stock insights.' }
          ]
        },
        {
          title: 'Operational Impact',
          type: 'stats',
          items: [
            { value: 'Real-time', label: 'Stock Tracking' },
            { value: 'Multi-site', label: 'Warehouse Control' },
            { value: 'Smart', label: 'Reorder Alerts' },
            { value: 'Accurate', label: 'Audit Support' }
          ]
        },
        {
          title: 'Benefits',
          type: 'grid',
          items: [
            { title: 'Reduced Wastage', description: 'Avoid unnecessary purchases and minimize dead stock through better planning.' },
            { title: 'Faster Operations', description: 'Speed up receiving, issuing, and verification workflows across teams.' },
            { title: 'Better Forecasting', description: 'Use historical consumption data to plan future demand more effectively.' },
            { title: 'Stronger Accountability', description: 'Maintain complete transaction history for every item, location, and user action.' }
          ]
        }
      ]
    })
  },
  'advertisement-management-system': {
    title: 'Advertisement Management System',
    content: (
      <div style={styles.mainContainer}>
        <div style={styles.container}>
          <div style={styles.hero}>
            <div style={styles.heroOverlay}></div>
            <div style={styles.heroContent}>
              <h1 style={styles.title}>Advertisement Management System</h1>
              <p style={styles.subtitle}>Transform your advertising operations with our comprehensive management solution that ensures efficiency, compliance, and optimal campaign performance.</p>
            </div>
          </div>

          <div style={styles.section}>
            <h2 style={styles.sectionTitle}>Introduction</h2>
            <p style={styles.featureDescription}>
              Advertisement Activity Management Software (AAMS) is a specialized solution designed to streamline and optimize advertising campaigns for agencies. It provides comprehensive tools for managing agencies, payments, and campaigns efficiently across both traditional and digital platforms.
            </p>
            <p style={styles.featureDescription}>
              Our software enables precise targeting and audience segmentation, flexible ad placement and scheduling, and supports various ad formats including text, images, videos, and interactive elements.
            </p>
          </div>

          <div style={styles.section}>
            <h2 style={styles.sectionTitle}>Key Features</h2>
            <div style={styles.featureGrid}>
              <div style={styles.featureCard}>
                <h3 style={styles.featureTitle}>Multi-Channel Management</h3>
                <p style={styles.featureDescription}>Execute advertisements across social media, display print, electronic media, and traditional platforms.</p>
              </div>
              <div style={styles.featureCard}>
                <h3 style={styles.featureTitle}>Automated Scheduling</h3>
                <p style={styles.featureDescription}>Schedule and automate ad placements with precise timing control.</p>
              </div>
              <div style={styles.featureCard}>
                <h3 style={styles.featureTitle}>Financial Management</h3>
                <p style={styles.featureDescription}>Transparent pricing structure and efficient payment tracking system.</p>
              </div>
              <div style={styles.featureCard}>
                <h3 style={styles.featureTitle}>Compliance Monitoring</h3>
                <p style={styles.featureDescription}>Ensure adherence to regulations and advertising policies.</p>
              </div>
            </div>
          </div>

          <div style={styles.section}>
            <h2 style={styles.sectionTitle}>Advertisement Types</h2>
            <div style={styles.highlight}>
              <div style={styles.featureGrid}>
                <div style={styles.featureCard}>
                  <h3 style={styles.featureTitle}>Hoarding</h3>
                  <p style={styles.featureDescription}>Large outdoor advertising displays.</p>
                </div>
                <div style={styles.featureCard}>
                  <h3 style={styles.featureTitle}>Banner</h3>
                  <p style={styles.featureDescription}>Flexible advertising displays.</p>
                </div>
                <div style={styles.featureCard}>
                  <h3 style={styles.featureTitle}>Unipol</h3>
                  <p style={styles.featureDescription}>Single-pole advertising structures.</p>
                </div>
                <div style={styles.featureCard}>
                  <h3 style={styles.featureTitle}>LED & KIOSK</h3>
                  <p style={styles.featureDescription}>Digital and interactive displays.</p>
                </div>
              </div>
            </div>
          </div>

          <div style={styles.section}>
            <h2 style={styles.sectionTitle}>Benefits</h2>
            <div style={styles.statsContainer}>
              <div style={styles.statCard}>
                <div style={styles.statNumber}>✓</div>
                <div style={styles.statLabel}>Centralized Control</div>
              </div>
              <div style={styles.statCard}>
                <div style={styles.statNumber}>✓</div>
                <div style={styles.statLabel}>Revenue Growth</div>
              </div>
              <div style={styles.statCard}>
                <div style={styles.statNumber}>✓</div>
                <div style={styles.statLabel}>Improved Efficiency</div>
              </div>
              <div style={styles.statCard}>
                <div style={styles.statNumber}>✓</div>
                <div style={styles.statLabel}>Regulatory Compliance</div>
              </div>
            </div>
          </div>

          <div style={styles.section}>
            <h2 style={styles.sectionTitle}>System Modules</h2>
            <div style={styles.featureGrid}>
              <div style={styles.featureCard}>
                <h3 style={styles.featureTitle}>Agency Management</h3>
                <p style={styles.featureDescription}>Register and manage advertising agencies.</p>
              </div>
              <div style={styles.featureCard}>
                <h3 style={styles.featureTitle}>License Management</h3>
                <p style={styles.featureDescription}>Online license renewal and tracking.</p>
              </div>
              <div style={styles.featureCard}>
                <h3 style={styles.featureTitle}>Challan System</h3>
                <p style={styles.featureDescription}>Online and offline challan creation.</p>
              </div>
              <div style={styles.featureCard}>
                <h3 style={styles.featureTitle}>Payment Tracking</h3>
                <p style={styles.featureDescription}>Comprehensive financial management.</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  },
  'digital-academic-system': {
    title: 'Digital Academic System (DAS-ERP)',
    content: renderProductContent({
      styles,
      heroTitle: 'Digital Academic System (DAS-ERP)',
      subtitle: 'Unify admissions, academics, examinations, finance, and communication through an ERP platform designed for schools, colleges, and educational institutions.',
      sections: [
        {
          title: 'Academic Transformation',
          paragraphs: [
            'Digital Academic System brings together academic and administrative processes on a single platform so institutions can manage daily operations with more transparency and less manual effort.',
            'It is designed to support students, faculty, administrators, and parents with streamlined workflows for admissions, attendance, fee collection, examinations, and progress tracking.'
          ]
        },
        {
          title: 'Institutional Modules',
          type: 'grid',
          items: [
            { title: 'Admission Management', description: 'Handle inquiry, application, admission, and onboarding processes digitally.' },
            { title: 'Student Information System', description: 'Maintain complete student profiles, class allocation, and academic records.' },
            { title: 'Examination and Results', description: 'Configure exams, publish marks, generate report cards, and track performance trends.' },
            { title: 'Fees and Accounts', description: 'Manage fee structures, receipts, dues, concessions, and financial reporting.' }
          ]
        },
        {
          title: 'Teaching and Administration Support',
          type: 'grid',
          highlight: true,
          items: [
            { title: 'Timetable and Scheduling', description: 'Plan classes, rooms, faculty allocation, and academic calendars efficiently.' },
            { title: 'Attendance and Leave', description: 'Track student and staff attendance with automated notifications and summaries.' },
            { title: 'Parent Communication', description: 'Share notices, performance updates, and alerts through connected communication channels.' },
            { title: 'Document and Certificate Management', description: 'Generate bonafide letters, ID cards, certificates, and official academic documents.' }
          ]
        },
        {
          title: 'Platform Value',
          type: 'stats',
          items: [
            { value: 'ERP', label: 'Integrated Platform' },
            { value: '360', label: 'Student View' },
            { value: 'Fast', label: 'Administrative Workflows' },
            { value: 'Live', label: 'Academic Reporting' }
          ]
        },
        {
          title: 'Benefits for Institutions',
          type: 'grid',
          items: [
            { title: 'Reduced Manual Work', description: 'Automate repetitive academic and administrative tasks across departments.' },
            { title: 'Improved Coordination', description: 'Keep management, faculty, students, and parents aligned with one system of record.' },
            { title: 'Data-driven Decisions', description: 'Use real-time dashboards to evaluate academic performance and operational health.' },
            { title: 'Scalable Operations', description: 'Support growing institutions with configurable workflows and role-based access.' }
          ]
        }
      ]
    })
  },
  'asset-management-system': {
    title: 'Asset Management System',
    content: (
      <div style={styles.mainContainer}>
        <div style={styles.container}>
          <div style={styles.hero}>
            <div style={styles.heroOverlay}></div>
            <div style={styles.heroContent}>
              <h1 style={styles.title}>Asset Management System</h1>
              <p style={styles.subtitle}>Transform your organization's asset management with our cutting-edge RFID-based solution that ensures accuracy, efficiency, and real-time tracking.</p>
            </div>
          </div>

          <div style={styles.section}>
            <h2 style={styles.sectionTitle}>Project Overview</h2>
            <p style={styles.featureDescription}>
              RFID (Radio Frequency Identification) technology uses RFID tags or labels to track and manage physical assets. RFID technology uses radio waves to communicate between a reader and a tag, which contains a unique identifier that can be associated with specific assets.
            </p>
            <p style={styles.featureDescription}>
              RFID technology offers several advantages over traditional asset management methods, such as barcodes and manual tracking. With RFID, assets can be tracked in real time, providing accurate and up-to-date information on their location and status. This enables organizations to improve inventory accuracy, reduce loss and theft, and optimize asset utilization.
            </p>
          </div>

          <div style={styles.section}>
            <h2 style={styles.sectionTitle}>Project Understanding and Its Scope</h2>
            <div style={styles.featureGrid}>
              <div style={styles.featureCard}>
                <h3 style={styles.featureTitle}>Requirement Analysis</h3>
                <p style={styles.featureDescription}>Understanding specific requirements related to asset management, including types of assets and desired accuracy levels.</p>
              </div>
              <div style={styles.featureCard}>
                <h3 style={styles.featureTitle}>System Design</h3>
                <p style={styles.featureDescription}>Designing RFID system including selection of tags, readers, and software components.</p>
              </div>
              <div style={styles.featureCard}>
                <h3 style={styles.featureTitle}>Implementation</h3>
                <p style={styles.featureDescription}>Installing RFID readers, attaching tags to assets, and integrating with existing systems.</p>
              </div>
              <div style={styles.featureCard}>
                <h3 style={styles.featureTitle}>Testing & Deployment</h3>
                <p style={styles.featureDescription}>Thorough testing and deployment with employee training for optimal system usage.</p>
              </div>
            </div>
          </div>

          <div style={styles.section}>
            <h2 style={styles.sectionTitle}>Major Modules</h2>
            <div style={styles.highlight}>
              <div style={styles.featureGrid}>
                <div style={styles.featureCard}>
                  <h3 style={styles.featureTitle}>Asset Type</h3>
                  <p style={styles.featureDescription}>Categorization and classification of different asset types.</p>
                </div>
                <div style={styles.featureCard}>
                  <h3 style={styles.featureTitle}>Asset Detail</h3>
                  <p style={styles.featureDescription}>Comprehensive asset information including purchase details and location.</p>
                </div>
                <div style={styles.featureCard}>
                  <h3 style={styles.featureTitle}>Asset Issue/Return</h3>
                  <p style={styles.featureDescription}>Tracking system for asset allocation and return processes.</p>
                </div>
                <div style={styles.featureCard}>
                  <h3 style={styles.featureTitle}>Reports</h3>
                  <p style={styles.featureDescription}>Various reporting modules including depreciation, geolocation, and conditional reports.</p>
                </div>
              </div>
            </div>
          </div>

          <div style={styles.section}>
            <h2 style={styles.sectionTitle}>Benefits</h2>
            <div style={styles.statsContainer}>
              <div style={styles.statCard}>
                <div style={styles.statNumber}>99.9%</div>
                <div style={styles.statLabel}>Asset Tracking Accuracy</div>
              </div>
              <div style={styles.statCard}>
                <div style={styles.statNumber}>50%</div>
                <div style={styles.statLabel}>Reduced Loss</div>
              </div>
              <div style={styles.statCard}>
                <div style={styles.statNumber}>100%</div>
                <div style={styles.statLabel}>Real-time Visibility</div>
              </div>
              <div style={styles.statCard}>
                <div style={styles.statNumber}>24/7</div>
                <div style={styles.statLabel}>Asset Monitoring</div>
              </div>
            </div>
          </div>

          <div style={styles.section}>
            <h2 style={styles.sectionTitle}>System Architecture</h2>
            <div style={styles.featureGrid}>
              <div style={styles.featureCard}>
                <h3 style={styles.featureTitle}>Database</h3>
                <p style={styles.featureDescription}>Centralized storage for all asset-related data and tracking information.</p>
              </div>
              <div style={styles.featureCard}>
                <h3 style={styles.featureTitle}>User Interface</h3>
                <p style={styles.featureDescription}>Intuitive web-based interface for easy asset management and monitoring.</p>
              </div>
              <div style={styles.featureCard}>
                <h3 style={styles.featureTitle}>RFID Infrastructure</h3>
                <p style={styles.featureDescription}>Network of RFID readers and tags for comprehensive asset tracking.</p>
              </div>
              <div style={styles.featureCard}>
                <h3 style={styles.featureTitle}>Security</h3>
                <p style={styles.featureDescription}>Robust security measures including encryption and access controls.</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  },
  'pole-street-light-management': {
    title: 'Pole and Street Light Management System',
    content: renderProductContent({
      styles,
      heroTitle: 'Pole and Street Light Management System',
      subtitle: 'Digitize pole and street light assets with centralized monitoring, maintenance tracking, and location intelligence for smarter urban infrastructure management.',
      sections: [
        {
          title: 'Solution Overview',
          paragraphs: [
            'This system helps municipal bodies and infrastructure teams maintain a complete digital record of poles, street lights, feeders, and related maintenance activities.',
            'By combining asset mapping, fault tracking, and field reporting, the platform improves uptime, speeds issue resolution, and creates better visibility across public lighting networks.'
          ]
        },
        {
          title: 'Core Features',
          type: 'grid',
          items: [
            { title: 'Asset Mapping', description: 'Maintain GIS-linked records for every pole, light, feeder, and installation point.' },
            { title: 'Complaint and Fault Logging', description: 'Register non-working lights, wiring issues, and field complaints with status tracking.' },
            { title: 'Maintenance Planning', description: 'Assign teams, schedule preventive maintenance, and monitor service completion.' },
            { title: 'Energy and Status Monitoring', description: 'Track operational status, consumption trends, and outage patterns across zones.' }
          ]
        },
        {
          title: 'Operational Modules',
          type: 'grid',
          highlight: true,
          items: [
            { title: 'Pole Registry', description: 'Store pole number, type, ownership, location, and connected assets in one place.' },
            { title: 'Street Light Inventory', description: 'Track fixture type, wattage, installation date, and maintenance history.' },
            { title: 'Field Workforce Module', description: 'Manage work orders, route allocation, and closure updates for maintenance crews.' },
            { title: 'Dashboard and Reports', description: 'Review zone-wise faults, resolution timelines, and infrastructure health summaries.' }
          ]
        },
        {
          title: 'Performance Snapshot',
          type: 'stats',
          items: [
            { value: 'GIS', label: 'Asset Mapping' },
            { value: 'Live', label: 'Issue Tracking' },
            { value: 'Zone-wise', label: 'Monitoring' },
            { value: 'Faster', label: 'Maintenance Response' }
          ]
        },
        {
          title: 'Benefits',
          type: 'grid',
          items: [
            { title: 'Better Infrastructure Visibility', description: 'Know exactly where assets are installed and what condition they are in.' },
            { title: 'Quicker Resolution', description: 'Reduce downtime with structured fault assignment and closure workflows.' },
            { title: 'Improved Public Service', description: 'Respond to citizen complaints more efficiently with location-aware tracking.' },
            { title: 'Planning Support', description: 'Use asset and maintenance data for budgeting, upgrades, and expansion projects.' }
          ]
        }
      ]
    })
  },
  'digital-library': {
    title: 'Digital Library / e-Library',
    content: renderProductContent({
      styles,
      heroTitle: 'Digital Library / e-Library',
      subtitle: 'Modernize library services with a digital platform that improves access to learning resources, simplifies administration, and supports hybrid reading environments.',
      sections: [
        {
          title: 'Library Modernization',
          paragraphs: [
            'Digital Library / e-Library transforms traditional library operations into a connected environment where users can discover, access, and manage resources more efficiently.',
            'The system supports digital catalogs, circulation, user membership, e-content access, and administrative control for academic, institutional, and public libraries.'
          ]
        },
        {
          title: 'Library Demo Videos',
          type: 'videos',
          items: [
            {
              title: 'Digital Library Walkthrough',
              url: 'https://www.youtube.com/embed/h41l-hSzOCI?si=FR3DV3RwCzqQkt3A',
              description: 'Place this first as the primary demo so visitors immediately see the core library experience and interface flow.'
            },
            {
              title: 'e-Library Feature Demo',
              url: 'https://www.youtube.com/embed/weEk5OA0F4o?si=AzNWNqq5lQRwfE1Z',
              description: 'Place this right beside the first video on desktop and below it on mobile as the secondary feature showcase.'
            }
          ]
        },
        {
          title: 'Key Capabilities',
          type: 'grid',
          items: [
            { title: 'Catalog Management', description: 'Organize books, journals, media, and digital resources with searchable metadata.' },
            { title: 'Membership and Circulation', description: 'Manage member registration, issue and return workflows, and overdue tracking.' },
            { title: 'Digital Resource Access', description: 'Provide secure access to e-books, PDFs, articles, and subscribed content collections.' },
            { title: 'Notifications and Reminders', description: 'Automate alerts for due dates, reservations, renewals, and announcements.' }
          ]
        },
        {
          title: 'User and Admin Modules',
          type: 'grid',
          highlight: true,
          items: [
            { title: 'Acquisition Management', description: 'Track procurement requests, approvals, and accessioning of new materials.' },
            { title: 'Circulation Desk', description: 'Handle issue, return, reservation, fine, and renewal processes efficiently.' },
            { title: 'Digital Repository', description: 'Store and present institution-specific learning materials and publications.' },
            { title: 'Analytics and Reports', description: 'Review circulation trends, most-used titles, membership activity, and collection gaps.' }
          ]
        },
        {
          title: 'Platform Highlights',
          type: 'stats',
          items: [
            { value: '24/7', label: 'Digital Access' },
            { value: 'Searchable', label: 'Resource Discovery' },
            { value: 'Centralized', label: 'Library Control' },
            { value: 'Hybrid', label: 'Print + Digital Support' }
          ]
        },
        {
          title: 'Benefits',
          type: 'grid',
          items: [
            { title: 'Enhanced User Experience', description: 'Help readers find and access resources quickly across formats.' },
            { title: 'Administrative Efficiency', description: 'Reduce manual ledger work and streamline everyday library processes.' },
            { title: 'Stronger Resource Utilization', description: 'Track demand patterns and make smarter collection development decisions.' },
            { title: 'Scalable Learning Support', description: 'Support classrooms, campuses, and remote users from the same platform.' }
          ]
        }
      ]
    })
  },
  'workflow-management-system': {
    title: 'Workflow Management System',
    content: renderProductContent({
      styles,
      heroTitle: 'Workflow Management System',
      subtitle: 'Standardize approvals, task movement, and document-driven processes with a workflow platform that improves accountability, turnaround time, and operational visibility.',
      sections: [
        {
          title: 'Process Digitization',
          paragraphs: [
            'Workflow Management System helps organizations convert manual movement of files, approvals, and internal tasks into well-defined digital processes.',
            'It enables departments to track progress, enforce approval hierarchies, and reduce delays by ensuring every task moves through the right path at the right time.'
          ]
        },
        {
          title: 'Functional Features',
          type: 'grid',
          items: [
            { title: 'Process Design', description: 'Configure approval chains, routing rules, service timelines, and escalation points.' },
            { title: 'Task Assignment', description: 'Assign work to individuals or teams with ownership, priorities, and due dates.' },
            { title: 'Status Tracking', description: 'Monitor pending, approved, rejected, and escalated items from centralized dashboards.' },
            { title: 'Notification Engine', description: 'Keep stakeholders informed with automated reminders, updates, and escalations.' }
          ]
        },
        {
          title: 'Workflow Modules',
          type: 'grid',
          highlight: true,
          items: [
            { title: 'Request Intake', description: 'Capture applications, service requests, or internal submissions in structured forms.' },
            { title: 'Approval Management', description: 'Move files through configurable review and sanction workflows.' },
            { title: 'Document Linkage', description: 'Attach supporting files and maintain contextual records at every step.' },
            { title: 'Audit and Reporting', description: 'Track bottlenecks, turnaround time, and user-wise productivity across workflows.' }
          ]
        },
        {
          title: 'System Snapshot',
          type: 'stats',
          items: [
            { value: 'Digital', label: 'Approval Trails' },
            { value: 'Timed', label: 'Escalation Rules' },
            { value: 'Live', label: 'Process Visibility' },
            { value: 'Role-based', label: 'Task Access' }
          ]
        },
        {
          title: 'Business Benefits',
          type: 'grid',
          items: [
            { title: 'Faster Turnaround', description: 'Reduce process delays by removing manual follow-ups and unclear ownership.' },
            { title: 'Higher Accountability', description: 'Capture every action, remark, and decision with user-level traceability.' },
            { title: 'Standardized Operations', description: 'Ensure departments follow consistent, policy-aligned process flows.' },
            { title: 'Improved Transparency', description: 'Give leadership and teams clear visibility into pending and completed work.' }
          ]
        }
      ]
    })
  },
  'document-management-system': {
    title: 'Document Management System',
    content: (
      <div style={styles.mainContainer}>
        <div style={styles.container}>
          <div style={styles.hero}>
            <div style={styles.heroOverlay}></div>
            <div style={styles.heroContent}>
              <h1 style={styles.title}>Document Management System</h1>
              <p style={styles.subtitle}>Transform your organization's document management with our comprehensive digital solution that ensures security, efficiency, and seamless collaboration.</p>
            </div>
          </div>

          <div style={styles.section}>
            <h2 style={styles.sectionTitle}>Introduction</h2>
            <p style={styles.featureDescription}>
              In today's rapidly evolving digital landscape, organizations increasingly recognize the necessity of efficient information management systems to meet operational demands. The exponential growth of data, coupled with the need for instant accessibility and secure storage, has made traditional paper-based document management systems obsolete.
            </p>
            <p style={styles.featureDescription}>
              Our Document Management System (DMS) is designed to digitize, centralize, and streamline document-related processes, enabling government organizations to achieve operational excellence, enhance collaboration, and meet the growing demands of a digitized world.
            </p>
          </div>

          <div style={styles.section}>
            <h2 style={styles.sectionTitle}>Key Features</h2>
            <div style={styles.featureGrid}>
              <div style={styles.featureCard}>
                <h3 style={styles.featureTitle}>Smart Chatbot Integration</h3>
                <p style={styles.featureDescription}>AI-powered virtual assistant for instant document search and retrieval, available 24/7.</p>
              </div>
              <div style={styles.featureCard}>
                <h3 style={styles.featureTitle}>Document Storage & Organization</h3>
                <p style={styles.featureDescription}>Centralized platform with hierarchical organization, tags, and metadata mapping.</p>
              </div>
              <div style={styles.featureCard}>
                <h3 style={styles.featureTitle}>Version Control</h3>
                <p style={styles.featureDescription}>Track changes, view history, and revert to previous versions of documents.</p>
              </div>
              <div style={styles.featureCard}>
                <h3 style={styles.featureTitle}>Advanced Security</h3>
                <p style={styles.featureDescription}>Role-based access control, encryption, and comprehensive audit trails.</p>
              </div>
            </div>
          </div>

          <div style={styles.section}>
            <h2 style={styles.sectionTitle}>System Modules</h2>
            <div style={styles.highlight}>
              <div style={styles.featureGrid}>
                <div style={styles.featureCard}>
                  <h3 style={styles.featureTitle}>Master Module</h3>
                  <p style={styles.featureDescription}>Central repository for configurations, departments, categories, and roles.</p>
                </div>
                <div style={styles.featureCard}>
                  <h3 style={styles.featureTitle}>Document Management</h3>
                  <p style={styles.featureDescription}>Core document handling with categorization, search, and access control.</p>
                </div>
                <div style={styles.featureCard}>
                  <h3 style={styles.featureTitle}>User Management</h3>
                  <p style={styles.featureDescription}>Comprehensive user and role management with authentication settings.</p>
                </div>
                <div style={styles.featureCard}>
                  <h3 style={styles.featureTitle}>Reports Module</h3>
                  <p style={styles.featureDescription}>Customizable analytics and insights with multiple export options.</p>
                </div>
              </div>
            </div>
          </div>

          <div style={styles.section}>
            <h2 style={styles.sectionTitle}>Implementation Plan</h2>
            <div style={styles.statsContainer}>
              <div style={styles.statCard}>
                <div style={styles.statNumber}>1 Week</div>
                <div style={styles.statLabel}>Requirements Gathering</div>
              </div>
              <div style={styles.statCard}>
                <div style={styles.statNumber}>12 Weeks</div>
                <div style={styles.statLabel}>Design & Development</div>
              </div>
              <div style={styles.statCard}>
                <div style={styles.statNumber}>2 Weeks</div>
                <div style={styles.statLabel}>Testing & QA</div>
              </div>
              <div style={styles.statCard}>
                <div style={styles.statNumber}>1 Week</div>
                <div style={styles.statLabel}>Deployment & Training</div>
              </div>
            </div>
          </div>

          <div style={styles.section}>
            <h2 style={styles.sectionTitle}>Technical Specifications</h2>
            <div style={styles.featureGrid}>
              <div style={styles.featureCard}>
                <h3 style={styles.featureTitle}>Front End</h3>
                <p style={styles.featureDescription}>ASP .NET, Framework 4.5</p>
              </div>
              <div style={styles.featureCard}>
                <h3 style={styles.featureTitle}>Middle Tier</h3>
                <p style={styles.featureDescription}>Microsoft IIS server</p>
              </div>
              <div style={styles.featureCard}>
                <h3 style={styles.featureTitle}>Database</h3>
                <p style={styles.featureDescription}>Microsoft SQL Server 2017 onwards</p>
              </div>
              <div style={styles.featureCard}>
                <h3 style={styles.featureTitle}>Reports</h3>
                <p style={styles.featureDescription}>Crystal Report</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  },
  'opac': {
    title: 'OPAC',
    content: renderProductContent({
      styles,
      heroTitle: 'OPAC',
      subtitle: 'Offer library users a fast, searchable, web-based public access catalog for discovering holdings, checking availability, and improving self-service access to collections.',
      sections: [
        {
          title: 'What OPAC Delivers',
          paragraphs: [
            'Online Public Access Catalog (OPAC) acts as the digital search gateway for library collections, allowing users to browse and locate materials without depending on manual catalog lookups.',
            'It improves discoverability for books, journals, reference materials, and digital resources while reducing pressure on front-desk staff through simple self-service search.'
          ]
        },
        {
          title: 'User Features',
          type: 'grid',
          items: [
            { title: 'Advanced Search', description: 'Search by title, author, subject, keyword, accession number, or publication details.' },
            { title: 'Availability Status', description: 'See whether an item is available, issued, reserved, or part of a digital collection.' },
            { title: 'Reservation Support', description: 'Allow members to place holds or request items directly from the catalog interface.' },
            { title: 'Account Access', description: 'Enable users to review issued items, due dates, and reservation activity.' }
          ]
        },
        {
          title: 'Catalog Administration',
          type: 'grid',
          highlight: true,
          items: [
            { title: 'Bibliographic Integration', description: 'Sync catalog records with the library management system for accurate listings.' },
            { title: 'Metadata-driven Discovery', description: 'Improve search precision with structured tags, classifications, and subject indexing.' },
            { title: 'Multi-branch Visibility', description: 'Show collection availability across campuses, departments, or library branches.' },
            { title: 'Usage Insights', description: 'Track search behavior and popular items to improve collection planning.' }
          ]
        },
        {
          title: 'Access Snapshot',
          type: 'stats',
          items: [
            { value: 'Web', label: 'Public Access' },
            { value: 'Instant', label: 'Catalog Search' },
            { value: 'Member', label: 'Self-service Features' },
            { value: 'Linked', label: 'Library Integration' }
          ]
        },
        {
          title: 'Benefits',
          type: 'grid',
          items: [
            { title: 'Better Discoverability', description: 'Make collections easier to explore for students, researchers, and members.' },
            { title: 'Reduced Manual Queries', description: 'Lower routine catalog assistance workload for library staff.' },
            { title: 'Improved User Convenience', description: 'Let users search from anywhere before visiting the library.' },
            { title: 'Stronger Collection Access', description: 'Increase usage of both physical and digital resources through better visibility.' }
          ]
        }
      ]
    })
  },
  'health-management-system': {
    title: 'Health Management System',
    content: renderProductContent({
      styles,
      heroTitle: 'Health Management System',
      subtitle: 'Coordinate patient records, appointments, clinical workflows, and operational reporting through a centralized healthcare management platform.',
      sections: [
        {
          title: 'Healthcare Operations Overview',
          paragraphs: [
            'Health Management System is built to support hospitals, clinics, wellness centers, and institutional health units with connected patient and administrative workflows.',
            'It improves care coordination by bringing registration, consultation, diagnostics, pharmacy, billing, and reporting into a structured digital environment.'
          ]
        },
        {
          title: 'Clinical and Administrative Features',
          type: 'grid',
          items: [
            { title: 'Patient Registration', description: 'Create digital patient records with demographic, medical, and visit history details.' },
            { title: 'Appointment and Queue Management', description: 'Schedule consultations, manage waiting lists, and optimize daily operations.' },
            { title: 'EMR and Consultation Notes', description: 'Maintain case history, prescriptions, vitals, and doctor observations securely.' },
            { title: 'Billing and Service Tracking', description: 'Handle consultation charges, diagnostics, pharmacy billing, and payment records.' }
          ]
        },
        {
          title: 'Core Modules',
          type: 'grid',
          highlight: true,
          items: [
            { title: 'Doctor and Staff Management', description: 'Configure provider schedules, departments, roles, and service availability.' },
            { title: 'Laboratory and Diagnostics', description: 'Track test requests, results entry, and report sharing workflows.' },
            { title: 'Pharmacy and Inventory', description: 'Monitor medicine stock, issue records, and replenishment needs.' },
            { title: 'MIS and Reporting', description: 'Generate patient, revenue, department, and utilization reports for management review.' }
          ]
        },
        {
          title: 'System Value',
          type: 'stats',
          items: [
            { value: 'Centralized', label: 'Patient Records' },
            { value: 'Faster', label: 'Appointment Handling' },
            { value: 'Secure', label: 'Medical Data Access' },
            { value: 'Integrated', label: 'Care Workflows' }
          ]
        },
        {
          title: 'Benefits',
          type: 'grid',
          items: [
            { title: 'Improved Patient Experience', description: 'Reduce waiting time and simplify registration, follow-up, and service coordination.' },
            { title: 'Better Clinical Continuity', description: 'Give care teams timely access to history, diagnostics, and treatment records.' },
            { title: 'Operational Efficiency', description: 'Connect front office, doctors, labs, and billing teams through one platform.' },
            { title: 'Management Visibility', description: 'Use reports and dashboards to monitor service delivery and facility performance.' }
          ]
        }
      ]
    })
  }
});

const SoftwareProductPage = () => {
  const [viewportWidth, setViewportWidth] = useState(() =>
    typeof window !== 'undefined' ? window.innerWidth : 1200
  );

  useEffect(() => {
    if (typeof window === 'undefined') {
      return undefined;
    }

    const handleResize = () => {
      setViewportWidth(window.innerWidth);
    };

    handleResize();
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  const styles = getResponsiveStyles(viewportWidth);
  const productDetails = getProductDetails(styles);

  const { productId } = useParams();
  const hardwareProduct = productCatalogBySlug[productId];
  const product = hardwareProduct
    ? {
        title: hardwareProduct.label,
        content: <HardwareProductContent product={hardwareProduct} styles={styles} />
      }
    : productDetails[productId];

  if (!product) {
    return <div style={styles.container}><h2>Product Not Found</h2></div>;
  }

  return (
    <div style={styles.mainContainer}>
      {product.content}
    </div>
  );
};

export default SoftwareProductPage; 
